import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Stack,
    Chip,
    Box,
} from "@mui/material";

function KeywordSection({ label, color, type, inputValue, setInputValue, placeholder, keywords, onAdd, onRemove }) {
    return (
        <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                    fullWidth
                    size="small"
                    label={label}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); onAdd(type, inputValue, setInputValue); }
                    }}
                    placeholder={placeholder}
                />
                <Button variant="outlined" color={color} onClick={() => onAdd(type, inputValue, setInputValue)}>
                    Ajouter
                </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {(keywords || []).map((kw) => (
                    <Chip key={kw} label={kw} size="small" color={color} variant="outlined" onDelete={() => onRemove(type, kw)} />
                ))}
            </Stack>
        </Box>
    );
}

export default function BackupTypeModal({ open, onClose, onSave, initialData, loading = false }) {
    const [form, setForm] = useState({
        name: "",
        dateSearchPattern: "",
        successKeywords: [],
        failureKeywords: [],
        alertKeywords: [],
    });

    const [successInput, setSuccessInput] = useState("");
    const [failureInput, setFailureInput] = useState("");
    const [alertInput, setAlertInput] = useState("");

    useEffect(() => {
        if (initialData) {
            setForm({
                name: initialData.name || "",
                dateSearchPattern: initialData.dateSearchPattern || "",
                successKeywords: initialData.successKeywords || [],
                failureKeywords: initialData.failureKeywords || [],
                alertKeywords: initialData.alertKeywords || [],
            });
        } else {
            setForm({ name: "", dateSearchPattern: "", successKeywords: [], failureKeywords: [], alertKeywords: [] });
        }
        setSuccessInput("");
        setFailureInput("");
        setAlertInput("");
    }, [initialData, open]);

    const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    const addKeyword = (type, value, setValue) => {
        if (!value.trim()) return;
        const field = type === "success" ? "successKeywords" : type === "failure" ? "failureKeywords" : "alertKeywords";
        setForm((prev) => ({ ...prev, [field]: [...(prev[field] || []), value.trim()] }));
        setValue("");
    };

    const removeKeyword = (type, keyword) => {
        const field = type === "success" ? "successKeywords" : type === "failure" ? "failureKeywords" : "alertKeywords";
        setForm((prev) => ({ ...prev, [field]: prev[field].filter((k) => k !== keyword) }));
    };

    const handleSave = () => {
        if (!form.name.trim()) {
            alert("Le nom du type est requis");
            return;
        }
        const finalSuccess = [...(form.successKeywords || [])];
        if (successInput.trim() && !finalSuccess.includes(successInput.trim())) finalSuccess.push(successInput.trim());
        const finalFailure = [...(form.failureKeywords || [])];
        if (failureInput.trim() && !finalFailure.includes(failureInput.trim())) finalFailure.push(failureInput.trim());
        const finalAlert = [...(form.alertKeywords || [])];
        if (alertInput.trim() && !finalAlert.includes(alertInput.trim())) finalAlert.push(alertInput.trim());

        onSave({
            ...form,
            dateSearchPattern: form.dateSearchPattern.trim() || null,
            successKeywords: finalSuccess.length ? finalSuccess : null,
            failureKeywords: finalFailure.length ? finalFailure : null,
            alertKeywords: finalAlert.length ? finalAlert : null,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 2 }}>
                {initialData ? "Modifier le type de sauvegarde" : "Ajouter un type de sauvegarde"}
            </DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Stack spacing={3}>
                    <TextField
                        fullWidth
                        label="Nom du type"
                        required
                        value={form.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="ex: Nakivo, Veeam, Acronis"
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        label="Pattern de recherche de date (regex optionnel)"
                        value={form.dateSearchPattern}
                        onChange={(e) => handleChange("dateSearchPattern", e.target.value)}
                        placeholder="ex: démarrer à\\s*(\\d{2}/\\d{2}/\\d{4}\\s+\\d{2}:\\d{2})"
                        InputLabelProps={{ shrink: true }}
                    />
                    <KeywordSection
                        label="Keyword de succès"
                        color="success"
                        type="success"
                        inputValue={successInput}
                        setInputValue={setSuccessInput}
                        placeholder="ex: Réussite, Success, Completed"
                        keywords={form.successKeywords}
                        onAdd={addKeyword}
                        onRemove={removeKeyword}
                    />
                    <KeywordSection
                        label="Keyword d'erreur"
                        color="error"
                        type="failure"
                        inputValue={failureInput}
                        setInputValue={setFailureInput}
                        placeholder="ex: Échec, Failed, Error"
                        keywords={form.failureKeywords}
                        onAdd={addKeyword}
                        onRemove={removeKeyword}
                    />
                    <KeywordSection
                        label="Keyword d'alerte"
                        color="warning"
                        type="alert"
                        inputValue={alertInput}
                        setInputValue={setAlertInput}
                        placeholder="ex: Warning, Avertissement"
                        keywords={form.alertKeywords}
                        onAdd={addKeyword}
                        onRemove={removeKeyword}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Annuler</Button>
                <Button onClick={handleSave} variant="contained" disabled={loading}>
                    {initialData ? "Mettre à jour" : "Créer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
